import { useState } from 'react';
import { useDrag } from 'react-dnd';
import { useScript, useUpdateScript } from './ScriptContext';
import { getNode } from './treeUtils';
import REGISTRY from '../blockRegistry';
import ExpressionSlot from './ExpressionSlot';
import StatementList from './StatementList';
import { ItemTypes } from './dndTypes';

// Inline field editor — renders the right input for each field type.
function BlockField({ path, field, textColor = '#000' }) {
  const { compId, comps, vars, externals, creationIndex } = useScript();
  const update = useUpdateScript();
  const node = getNode(comps[compId].script, path);
  const value = node[field.key];

  const onChange = (newVal) => {
    update((script) => { getNode(script, path)[field.key] = newVal; });
  };

  const inputStyle = {
    fontSize: '0.8vmax',
    padding: '2px 6px',
    border: '1px solid rgba(0,0,0,0.4)',
    borderRadius: '3px',
    backgroundColor: 'rgba(255,255,255,0.9)',
    color: '#000',
    fontFamily: 'Work Sans, sans-serif',
    minWidth: '60px',
    maxWidth: '120px',
  };

  // When a field defines inputWidth, pin both min and max to that value.
  const sizeOverride = field.inputWidth
    ? { minWidth: field.inputWidth, maxWidth: field.inputWidth, width: field.inputWidth }
    : {};

  if (field.type === 'text' || field.type === 'number') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <input
          type={field.type === 'number' ? 'number' : 'text'}
          className="panel-input"
          value={value ?? ''}
          placeholder={field.placeholder || ''}
          onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        />
      </label>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px', cursor: 'pointer' }}>
        <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
          {value === 'true' ? 'TRUE' : 'FALSE'}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onChange(value === 'true' ? 'false' : 'true'); }}
          style={{
            ...inputStyle, cursor: 'pointer', padding: '2px 8px',
            backgroundColor: value === 'true' ? '#00b894' : '#d63031',
            color: '#fff', border: 'none',
          }}
        >
          {value === 'true' ? 'true' : 'false'}
        </button>
      </label>
    );
  }

  if (field.type === 'color') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor }}>{field.label}</span>
        )}
        <input
          type="color"
          value={value || '#ffffff'}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          className="panel-color-input"
        />
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          {(field.options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'var-select') {
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          <option value="">— none —</option>
          {(vars || []).map((v) => (
            <option key={v.name} value={v.name}>{v.name}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'table-var-select') {
    // Table variable blocks operate on the owning InputTable's own variables,
    // not the form-level vars. The interpreter (execute_script) resolves `var`
    // via parseInt as an index into comps[id].vars[row], so the stored value
    // is the variable's index in the table's definition row (vars[0]).
    const tableVars = Array.isArray(comps[compId]?.vars?.[0]) ? comps[compId].vars[0] : [];
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          <option value="">— none —</option>
          {tableVars.map((v, i) => (
            <option key={i} value={String(i)}>{v.name}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'flag-select') {
    const flags = (externals && externals.flags) ? externals.flags : [];
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          <option value="">— none —</option>
          {flags.map((f) => (
            <option key={f.name || f} value={f.name || f}>{f.name || f}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'flag-value-select') {
    // Lists the possible values of the flag chosen in this block's `flag` field,
    // so the stored value matches what the interpreter looks up.
    const flags = (externals && externals.flags) ? externals.flags : [];
    const selectedFlag = flags.find((f) => (f.name || f) === node.flag);
    const flagValues = selectedFlag && Array.isArray(selectedFlag.values) ? selectedFlag.values : [];
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          <option value="">— none —</option>
          {flagValues.map((v) => (
            <option key={v} value={v}>{v}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === 'component-select') {
    // The interpreter (execute_script) matches the target by creation_index when
    // the form uses them (creationIndex !== -1), otherwise by array index. Build
    // the option values the same way so the stored `comp` resolves at runtime.
    const usesCreationIndex = creationIndex !== -1 && creationIndex !== undefined && creationIndex !== null;
    const components = (comps || [])
      .map((c, i) => ({
        value: usesCreationIndex ? c?.creation_index : i,
        label: c?.name || c?.type || `Element ${i}`,
        type: c?.type,
      }))
      // Skip targets without a usable id, and InputTables (their disable state
      // is per-cell, handled by the dedicated table signals).
      .filter((c) => c.value !== undefined && c.value !== null && c.type !== 'intermediate/InputTable');
    return (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginRight: '8px' }}>
        {field.label && (
          <span style={{ fontSize: '0.75vmax', color: textColor, fontFamily: 'Work Sans, sans-serif' }}>
            {field.label}
          </span>
        )}
        <select
          className="panel-dropdown"
          value={value ?? ''}
          onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
          onClick={(e) => e.stopPropagation()}
          style={{ ...inputStyle, minWidth: '80px', ...sizeOverride }}
        >
          <option value="">— none —</option>
          {components.map((c) => (
            <option key={String(c.value)} value={String(c.value)}>{c.label}</option>
          ))}
        </select>
      </label>
    );
  }

  return null;
}

// The generic block component.
// path: full path array from script root to this node.
export default function Block({ path, depth = 0, breadcrumb = [] }) {
  const { compId, comps, setClipboard } = useScript();
  const update = useUpdateScript();
  const [commentOpen, setCommentOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // Resolve node and definition before any hook that depends on them,
  // but all hooks must be called unconditionally first.
  let node = null;
  try { node = getNode(comps[compId].script, path); } catch (_) {}
  const def = node?.type ? REGISTRY[node.type] : null;
  const isStatement = def ? def.kind === 'statement' : false;
  const itemType = isStatement ? ItemTypes.STATEMENT : ItemTypes.EXPRESSION;

  const [{ isDragging }, drag, dragPreview] = useDrag(() => ({
    type: itemType,
    item: () => ({ origin: 'canvas', path: [...path], blockType: node?.type }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [path, node?.type, itemType]);

  // Early returns AFTER all hooks
  if (!node || !node.type) return null;
  if (!def) {
    return (
      <div style={{
        display: 'inline-block', verticalAlign: 'top',
        margin: '4px', padding: '6px 10px',
        background: '#e5e7eb', color: '#374151',
        borderRadius: '4px', fontSize: '0.8vmax',
        fontFamily: 'Work Sans, sans-serif',
      }}>
        Unknown: {node.type}
      </div>
    );
  }

  const hasComment = node.comment !== -1 && node.comment !== undefined;
  const showComment = hasComment || commentOpen;

  // Compute slots: handle variable-arity (max/min)
  let slots = def.slots || [];
  if (def.variableArity) {
    const count = node.numOfChildren || node.children?.length || 2;
    slots = Array.from({ length: count }, (_, i) => ({
      label: `Input ${i + 1}`, kind: 'expression', via: i,
    }));
  }

  // Helpers for up/down movement
  const parentPath = path.slice(0, -1);
  const indexInParent = path[path.length - 1];
  const canMoveUp = isStatement && indexInParent > 0;
  const canMoveDown = isStatement && (() => {
    try {
      const parentNode = getNode(comps[compId].script, parentPath);
      return indexInParent < parentNode.children.length - 1;
    } catch { return false; }
  })();

  const moveUp = (e) => {
    e.stopPropagation();
    if (!canMoveUp) return;
    update((script) => {
      const parent = getNode(script, parentPath);
      const arr = parent.children;
      [arr[indexInParent - 1], arr[indexInParent]] = [arr[indexInParent], arr[indexInParent - 1]];
    });
  };

  const moveDown = (e) => {
    e.stopPropagation();
    if (!canMoveDown) return;
    update((script) => {
      const parent = getNode(script, parentPath);
      const arr = parent.children;
      [arr[indexInParent + 1], arr[indexInParent]] = [arr[indexInParent], arr[indexInParent + 1]];
    });
  };

  const deleteBlock = (e) => {
    e.stopPropagation();
    update((script) => {
      const parent = getNode(script, parentPath);
      parent.children.splice(indexInParent, 1);
    });
  };

  const copyBlock = (e) => {
    e.stopPropagation();
    setClipboard({ node: JSON.parse(JSON.stringify(node)), kind: def.kind });
  };

  const toggleCollapse = (e) => {
    e.stopPropagation();
    setCollapsed(c => !c);
  };

  const toggleComment = (e) => {
    e.stopPropagation();
    update((script) => {
      const n = getNode(script, path);
      n.comment = n.comment === -1 ? '' : -1;
    });
    setCommentOpen((o) => !o);
  };

  const addVariadicSlot = (e) => {
    e.stopPropagation();
    update((script) => {
      const n = getNode(script, path);
      n.children.push({ children: [] });
      n.numOfChildren = n.children.length;
    });
  };

  const removeVariadicSlot = (e) => {
    e.stopPropagation();
    if ((node.numOfChildren || node.children?.length || 2) <= 2) return;
    update((script) => {
      const n = getNode(script, path);
      n.children.pop();
      n.numOfChildren = n.children.length;
    });
  };

  const controlBtn = (label, onClick, title) => (
    <button
      title={title || label}
      onClick={onClick}
      style={{
        fontSize: '0.7vmax',
        padding: '2px 6px',
        marginLeft: '4px',
        border: `1px solid ${def.textColor ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'}`,
        borderRadius: '3px',
        backgroundColor: def.textColor ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
        color: def.textColor || '#000',
        cursor: 'pointer',
        fontFamily: 'Work Sans, sans-serif',
        lineHeight: 1.2,
      }}
    >
      {label}
    </button>
  );

  const bodyBg = 'rgba(0,0,0,0.025)';
  const hasSlots = slots.length > 0;

  // Shared comment field — italic, code-comment style, accented with the block's own color
  const commentField = showComment && (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      padding: '3px 6px 3px 8px',
      borderLeft: `3px solid ${def.color}`,
      borderRadius: '0 3px 3px 0',
      backgroundColor: 'rgba(0,0,0,0.018)',
      marginTop: hasSlots ? '8px' : '0',
    }}>
      <span style={{
        fontFamily: 'monospace',
        fontSize: '0.68vmax',
        color: '#9ca3af',
        userSelect: 'none',
        flexShrink: 0,
        lineHeight: 1.4,
      }}>
        //
      </span>
      <input
        placeholder="add a comment…"
        value={hasComment ? node.comment : ''}
        onChange={(e) => {
          e.stopPropagation();
          update((script) => { getNode(script, path).comment = e.target.value; });
        }}
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: '0.72vmax',
          fontStyle: 'italic',
          fontFamily: 'Work Sans, sans-serif',
          color: '#4b5563',
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          padding: 0,
        }}
      />
    </div>
  );

  return (
    <div
      ref={dragPreview}
      style={{
        display: 'inline-block',
        verticalAlign: 'top',
        margin: isStatement ? '4px 0' : '2px',
        opacity: isDragging ? 0.35 : 1,
        transition: 'opacity 0.15s',
        maxWidth: isStatement ? '100%' : undefined,
        width: isStatement ? '100%' : undefined,
      }}
    >
      {/* Header bar */}
      <div
        ref={drag}
        style={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: def.color,
          color: def.textColor || '#000',
          padding: '4px 8px',
          borderRadius: (hasSlots && !collapsed) || (!hasSlots && showComment) ? '4px 4px 0 0' : '4px',
          cursor: 'grab',
          userSelect: 'none',
          flexWrap: 'wrap',
          gap: '4px',
          minWidth: isStatement ? undefined : '60px',
        }}
      >
        {/* Collapse chevron — only on blocks that have a body */}
        {hasSlots && (
          <button
            onClick={toggleCollapse}
            title={collapsed ? 'Expand' : 'Collapse'}
            style={{
              fontSize: '0.6vmax',
              padding: '1px 5px',
              marginRight: '2px',
              border: 'none',
              borderRadius: '3px',
              backgroundColor: 'transparent',
              color: def.textColor || '#000',
              cursor: 'pointer',
              lineHeight: 1,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s ease',
            }}
          >
            ▼
          </button>
        )}

        {/* Label */}
        <span style={{
          fontFamily: 'Work Sans, sans-serif',
          fontWeight: 600,
          fontSize: '0.8vmax',
          marginRight: '4px',
          whiteSpace: 'nowrap',
        }}>
          {def.shortLabel}
        </span>

        {/* Inline fields */}
        {(def.fields || []).map((field) => (
          <BlockField key={field.key} path={path} field={field} textColor={def.textColor || '#000'} />
        ))}

        {/* Controls */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
          {isStatement && canMoveUp   && controlBtn('↑', moveUp,   'Move up')}
          {isStatement && canMoveDown && controlBtn('↓', moveDown, 'Move down')}
          {def.variableArity && controlBtn('+', addVariadicSlot,    'Add input')}
          {def.variableArity && controlBtn('−', removeVariadicSlot, 'Remove input')}
          {controlBtn('⧉', copyBlock, 'Copy')}
          {controlBtn('✎', toggleComment, 'Comment')}
          {controlBtn('✕', deleteBlock, 'Delete')}
        </div>
      </div>

      {/* Body: slots — hidden when collapsed */}
      {hasSlots && !collapsed && (
        <div style={{
          padding: '8px 10px',
          backgroundColor: bodyBg,
          border: `2px solid ${def.color}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          display: isStatement ? 'block' : 'inline-block',
        }}>
          {slots.map((slot, si) => {
            // Compute the container path for this slot
            const containerPath = slot.via === null || slot.via === undefined
              ? path                        // flat: node.children IS the slot
              : [...path, slot.via];        // wrapped: node.children[via].children

            if (slot.kind === 'expression') {
              return (
                <div key={si} style={{
                  marginBottom: si < slots.length - 1 ? '8px' : '0',
                  display: 'inline-block',
                  marginRight: si < slots.length - 1 ? '12px' : '0',
                  verticalAlign: 'top',
                }}>
                  <ExpressionSlot
                    containerPath={containerPath}
                    label={slots.length > 1 ? slot.label : undefined}
                  />
                </div>
              );
            }

            // Statement slot
            return (
              <div key={si} style={{ marginBottom: si < slots.length - 1 ? '12px' : '0' }}>
                <div style={{
                  fontSize: '0.72vmax',
                  color: '#6b7280',
                  opacity: 1,
                  marginBottom: '4px',
                  fontFamily: 'Work Sans, sans-serif',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}>
                  {slot.label}
                </div>
                <div style={{
                  paddingLeft: '12px',
                  borderLeft: `3px solid ${def.color}`,
                }}>
                  <StatementList
                    containerPath={containerPath}
                    depth={depth + 1}
                    breadcrumb={[...breadcrumb, def.shortLabel, slot.label].filter(Boolean)}
                  />
                </div>
              </div>
            );
          })}

          {commentField}
        </div>
      )}

      {/* Comment strip when no body slots */}
      {!hasSlots && showComment && (
        <div style={{
          padding: '4px 0 4px 0',
          backgroundColor: bodyBg,
          border: `2px solid ${def.color}`,
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          paddingRight: '6px',
        }}>
          {commentField}
        </div>
      )}
    </div>
  );
}
